import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ComponentStore } from '@ngrx/component-store';
import { assertTrue, typedClone } from '@siza-staff-care/shared/utils';
import { Observable, tap, withLatestFrom } from 'rxjs';
import { IStore, IStore_Internal } from './store-interface';

const DEBUG = false;

interface GlobalStoreRegistryState {
	stores: IStore[];
	forceHasUnsavedChanges: boolean;
	openModalCount: number;
}

const defaultState: GlobalStoreRegistryState = {
	stores: [],
	forceHasUnsavedChanges: false,
	openModalCount: 0,
};

@Injectable({
	providedIn: 'root',
})
export class GlobalStoreRegistryStore extends ComponentStore<GlobalStoreRegistryState> {
	constructor(private dialogs: MatDialog) {
		super(typedClone(defaultState));
	}

	private readonly stores$ = this.select(({ stores }) => stores);
	private readonly openModalCount$ = this.select(({ openModalCount }) => openModalCount);

	/**
	 * Resets the store registry.
	 */
	readonly reset = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.setState(typedClone(defaultState));
			}),
		),
	);

	readonly setForceHasUnsavedChanges = this.effect((hasUnsavedChanges$: Observable<boolean>) =>
		hasUnsavedChanges$.pipe(
			tap((hasUnsavedChanges) => {
				if (DEBUG) console.log('setForceHasUnsavedChanges', hasUnsavedChanges);

				this.patchState({ forceHasUnsavedChanges: hasUnsavedChanges });
			}),
		),
	);

	/**
	 * Registers a store.
	 */
	readonly registerStore = this.effect((store$: Observable<IStore_Internal>) =>
		store$.pipe(
			withLatestFrom(this.stores$),
			tap(([store, stores]) => {
				if (DEBUG) console.log('registerStore', store.niceName);

				this.patchState({
					stores: [...stores, store],
				});
			}),
		),
	);

	/**
	 * Deregisters a store.
	 */
	readonly deregisterStore = this.effect((store$: Observable<IStore>) =>
		store$.pipe(
			tap((store) => {
				if (DEBUG) console.log('deregisterStore', store.niceName);

				this.patchState({
					stores: this.get().stores.filter((s) => s !== store),
				});
			}),
		),
	);

	readonly increaseOpenModalCount = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.openModalCount$),
			tap(([, openModalCount]) => {
				openModalCount++;
				this.patchState({ openModalCount });

				assertTrue(openModalCount > 0, 'openModalCount was negative!');
			}),
		),
	);

	readonly decreaseOpenModalCount = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.openModalCount$),
			tap(([, openModalCount]) => {
				openModalCount--;
				this.patchState({ openModalCount });

				assertTrue(openModalCount >= 0, 'openModalCount is negative!');
			}),
		),
	);

	/**
	 * Checks whether the user should be warned about potential unsaved changes:
	 * - If there's a store with unsaved changes (or the force flag is set)
	 * - If a dialog is open
	 * - Is a slide-out panel (modal) is open
	 *
	 * @returns
	 */
	hasUnsavedChangesOrDialogOrModalShowing() {
		const { stores, forceHasUnsavedChanges, openModalCount } = this.get();

		const openDialogsCount = this.dialogs.openDialogs.length;

		return (
			forceHasUnsavedChanges ||
			openDialogsCount > 0 ||
			openModalCount > 0 ||
			stores.some((store) => store.hasUnsavedChanges())
		);
	}
}
